// src/pages/PostEdit.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { fetchPostsByAccount, updatePost, attachPostImage } from "../../api/posts"; // ✅ changed
import { fetchForm } from "../../api/forms";
import { uploadToImagekit } from "../../api/images";

import FormRenderer from "../../components/FormRenderer";
import { getCroppedBlob } from "../../utils/cropImage";
import { getEditableProfileFormFields } from "../../utils/planEntitlements";

import { useAuth } from "../../auth";

export default function PostEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ include subscription so we can read accountId / domain / etc
  const { imagekitFolder, subscriptionLoading, subscription } = useAuth();

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ wait for subscription
        if (subscriptionLoading) return;

        // ✅ get accountId from subscription (supports both shapes)
        const accountId = subscription?.account?.id;

        if (!accountId) throw new Error("Missing accountId in subscription");

        const data = await fetchPostsByAccount(accountId); // ✅ changed
        const list = Array.isArray(data)
          ? data
          : data?.result || data?.posts || [];

        const post = list.find((p) => String(p.id || p.postId) === String(id));
        if (!post) throw new Error("Post not found");

        if (!alive) return;
        setDraft(post);

        const kind = post.kind?.trim();
        if (!kind) throw new Error("Post is missing kind");

        const f = await fetchForm(`post-${kind}`);
        if (!alive) return;
        if (kind === "attraction") {
          setForm({
            ...f,
            fields: (f.fields || []).filter((field) => field?.name !== "canonical"),
          });
        } else if (kind === "profile") {
          setForm({
            ...f,
            fields: getEditableProfileFormFields(f, subscription),
          });
        } else {
          setForm(f);
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load post");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, subscriptionLoading, subscription]); // ✅ include subscription state

  async function buildUploadFileFromDraftImage(img) {
    const f = img?.file;

    const hasRealFile =
      f instanceof Blob || (typeof File !== "undefined" && f instanceof File);

    if (!hasRealFile) return null;

    if (img.cropPixels) {
      const src = URL.createObjectURL(f);
      try {
        const mimeType = img.cropMimeType || "image/jpeg";
        const extension = mimeType === "image/png" ? "png" : "jpg";
        const blob = await getCroppedBlob(src, img.cropPixels, {
          mimeType,
          quality: 0.92,
        });

        const base = (f.name || "image").replace(/\.[^.]+$/, "");
        return new File([blob], `${base}.${extension}`, { type: mimeType });
      } finally {
        URL.revokeObjectURL(src);
      }
    }

    return f;
  }

  const onSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (subscriptionLoading) {
        throw new Error("Subscription still loading—try again in a moment.");
      }

      let nextDraft = { ...draft };
      const persistImageField = async (fieldName) => {
        const imageValue = nextDraft[fieldName];
        const alreadyPersisted = Boolean(imageValue?.url && imageValue?.fileId);
        if (!imageValue || alreadyPersisted) return;

        const uploadFile = await buildUploadFileFromDraftImage(imageValue);

        if (uploadFile) {
          const folder = imagekitFolder;

          const uploaded = await uploadToImagekit({
            file: uploadFile,
            fileName: uploadFile.name,
            folder,
            tags: [`post-${fieldName}`],
          });

          if (!uploaded?.url || !uploaded?.fileId) {
            throw new Error("ImageKit upload did not return url/fileId.");
          }

          const payload = {
            url: uploaded.url,
            fileId: uploaded.fileId,
            name: uploaded.name || uploadFile.name,
            width: uploaded.width,
            height: uploaded.height,
            thumbnailUrl: uploaded.thumbnailUrl,
          };

          if (fieldName === "image") {
            await attachPostImage(id, payload);
            nextDraft.imageUrl = payload.url;
          }
          nextDraft[fieldName] = payload;
        } else {
          delete nextDraft[fieldName];
          if (fieldName === "image") delete nextDraft.imageUrl;
        }
      };

      await persistImageField("image");
      await persistImageField("logo");

      await updatePost(id, nextDraft);
      navigate(`/posts/${nextDraft.kind || 'profile'}`);
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (error) return <div style={{ padding: 24, color: "red" }}>{error}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: "0 auto" }}>
      {form ? (
        <div>
          <h2>{form.label || form.title}</h2>
          <FormRenderer
            form={form}
            values={draft}
            onChange={setDraft}
            disabled={saving}
          />
        </div>
      ) : (
        <div style={{ color: "#666" }}>No form found</div>
      )}

      <div style={{ height: 16 }} />

      <button
        onClick={onSave}
        disabled={saving}
        style={{
          background: "#0b5fff",
          color: "white",
          padding: "8px 16px",
          borderRadius: 6,
          border: "none",
        }}
      >
        {saving ? "Saving..." : "Save"}
      </button>

      <button
        onClick={() => navigate(`/posts/${draft.kind || 'profile'}`)}
        disabled={saving}
        style={{
          marginLeft: 10,
          padding: "8px 16px",
          borderRadius: 6,
          border: "1px solid #ccc",
          background: "white",
        }}
      >
        Cancel
      </button>
    </div>
  );
}
